/**
 * Main application component with routing.
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Dashboard from './components/dashboard/Dashboard';
import HomePage from './pages/HomePage';
import DatasetsPage from './pages/DatasetsPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import AccessRequestPage from './pages/AccessRequestPage';
import { BACKEND_URL } from './config/settings';
import AdminRoute from './components/auth/AdminRoute';
import { getVersion } from './utils/version';
import { ErrorBoundary } from './components/common/ErrorBoundary';

// Allowed redirect paths to prevent open redirect vulnerabilities
const ALLOWED_REDIRECT_PATHS = [
  '/home',
  '/datasets',
  '/orders',
  '/profile',
  '/admin',
  '/access-request',
];

/**
 * Validates redirect path to prevent open redirect attacks.
 * Only allows redirects to known internal paths.
 */
function validateRedirectPath(path: string | null | undefined): string {
  if (!path) {
    return '/home';
  }

  // Remove query string and hash for validation
  const normalizedPath = path.split('?')[0].split('#')[0];

  // Check if path starts with allowed redirect
  const isAllowed = ALLOWED_REDIRECT_PATHS.some((allowedPath) =>
    normalizedPath === allowedPath || normalizedPath.startsWith(allowedPath + '/')
  );

  if (isAllowed) {
    return normalizedPath;
  }

  // Log warning for security monitoring
  console.warn(`Invalid redirect path attempted: ${path}, defaulting to /home`);
  return '/home';
}

function AppRoutes() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, logout, isAuthenticated } = useAuth();
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  // Track processed OAuth codes to prevent duplicate calls (StrictMode, re-renders)
  const processedOAuthCodes = useRef<Set<string>>(new Set());

  const handleOAuthCallback = useCallback(async (oauthCode: string, redirectPath: string) => {
    setIsProcessingAuth(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/auth/google/exchange-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies in request
        body: JSON.stringify({ oauth_code: oauthCode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to exchange OAuth token' }));
        console.error('OAuth token exchange failed:', errorData.detail);
        // Clean URL before redirecting
        window.history.replaceState({}, '', '/login');
        setIsProcessingAuth(false);
        navigate('/login', { replace: true });
        return;
      }

      const data = await response.json();
      if (data.access_token) {
        // Backend sets the httponly cookie via Set-Cookie header
        // Cookie is httponly so JavaScript cannot read it - just update auth state
        console.log('OAuth token exchange successful - backend httponly cookie set');

        // Update auth state - backend cookie will be sent automatically on next API call
        login();

        // Clean URL by removing OAuth callback parameters
        const currentPath = window.location.pathname;
        window.history.replaceState({}, '', currentPath);

        // Wait a short time for cookie to be set and auth state to update
        // Then navigate - the auth context will handle the actual auth verification
        // Using a small delay is more reliable than polling since we can't directly check
        // httponly cookies, and the login() call above triggers the auth state update
        setTimeout(() => {
          setIsProcessingAuth(false);
          navigate(redirectPath, { replace: true });
        }, 150);
      } else {
        console.error('No access token in OAuth response');
        window.history.replaceState({}, '', '/login');
        setIsProcessingAuth(false);
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('OAuth callback failed:', error);
      window.history.replaceState({}, '', '/login');
      setIsProcessingAuth(false);
      navigate('/login', { replace: true });
    }
  }, [login, navigate]);

  const handleMagicLinkVerification = useCallback(async (token: string, redirectPath: string) => {
    setIsProcessingAuth(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/auth/verify/${token}`, {
        credentials: 'include', // Include cookies in request
      });
      if (response.ok) {
        const data = await response.json();
        if (data.access_token) {
          // Backend should set cookie, but if it doesn't, we need to handle it
          // For now, just update auth state - backend cookie should be set
          login();

          // Clean URL by removing verification token
          const currentPath = window.location.pathname;
          window.history.replaceState({}, '', currentPath);

          // Wait a short time for cookie to be set and auth state to update
          // Then navigate - the auth context will handle the actual auth verification
          setTimeout(() => {
            setIsProcessingAuth(false);
            navigate(redirectPath, { replace: true });
          }, 150);
        } else {
          setIsProcessingAuth(false);
        }
      } else {
        setIsProcessingAuth(false);
      }
    } catch (error) {
      console.error('Magic link verification failed:', error);
      setIsProcessingAuth(false);
    }
  }, [login, navigate]);

  // Handle OAuth callback
  useEffect(() => {
    const oauthCode = searchParams.get('oauth_code');
    // Guard: Only process OAuth code once per code
    // This prevents duplicate calls from StrictMode, re-renders, or effect dependency changes
    if (oauthCode && !processedOAuthCodes.current.has(oauthCode)) {
      // Mark as processed immediately to prevent race conditions
      processedOAuthCodes.current.add(oauthCode);
      // Get and validate redirect path from location state
      const redirectPath = validateRedirectPath(location.state?.from?.pathname);
      handleOAuthCallback(oauthCode, redirectPath);
    }

    // Handle logout completion
    const logoutParam = searchParams.get('logout');
    if (logoutParam === 'complete') {
      const handleLogout = async () => {
        await logout();
        window.location.replace('/login');
      };
      handleLogout();
    }

    // Handle magic link verification
    const verifyToken = searchParams.get('verify');
    if (verifyToken) {
      // Get and validate redirect path from location state
      const redirectPath = validateRedirectPath(location.state?.from?.pathname);
      handleMagicLinkVerification(verifyToken, redirectPath);
    }
  }, [searchParams, logout, handleOAuthCallback, handleMagicLinkVerification, location]);

  // Show login page if not authenticated
  if (!isAuthenticated && location.pathname !== '/login') {
    return <Login isProcessingAuth={isProcessingAuth} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      >
        <Route path="/home" element={<HomePage />} />
        <Route path="/datasets" element={<DatasetsPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/access-request" element={<AccessRequestPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route path="/login" element={<Login isProcessingAuth={isProcessingAuth} />} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

// RTL language codes
const RTL_LANGUAGES = ['ar', 'fa', 'he', 'ku'];

function App() {
  const { i18n } = useTranslation();

  // Pre-fetch version on app load for faster tooltip display
  useEffect(() => {
    getVersion().catch(() => {
      // Silently fail - version will be fetched when needed
    });
  }, []);

  // Update HTML lang and dir attributes when language changes
  useEffect(() => {
    const currentLanguage = i18n.language.split('-')[0] || 'en';
    const isRTL = RTL_LANGUAGES.includes(currentLanguage);

    document.documentElement.lang = currentLanguage;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.body.dir = isRTL ? 'rtl' : 'ltr';

    // Add/remove RTL class for CSS styling
    if (isRTL) {
      document.documentElement.classList.add('rtl');
      document.body.classList.add('rtl');
    } else {
      document.documentElement.classList.remove('rtl');
      document.body.classList.remove('rtl');
    }
  }, [i18n.language]);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
